import { type ApiContext, anonymous, withToken } from "@hobbyshopify/backend-client";
import { loadConfig } from "@hobbyshopify/vite-cfg";
import { initializeApp } from "firebase/app";
import {
  type Auth,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  getAuth,
  isSignInWithEmailLink,
  OAuthProvider,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { ChangeNotifier } from "subtree.js/core";
import type { MerchantSession, SessionStage } from "../core/auth/merchant-session";

const MAGIC_LINK_EMAIL_KEY = "magicLinkEmail";

/**
 * Firebase web config is public. Sourced via @hobbyshopify/vite-cfg: inlined from
 * Vite env (VITE_FIREBASE_*) in local dev, or injected at runtime from
 * public/config.js when running in Docker.
 */
export function createFirebaseAuth(): Auth {
  const app = initializeApp({
    apiKey: loadConfig("VITE_FIREBASE_API_KEY"),
    authDomain: loadConfig("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: loadConfig("VITE_FIREBASE_PROJECT_ID"),
    appId: loadConfig("VITE_FIREBASE_APP_ID"),
  });
  return getAuth(app);
}

/**
 * The Firebase session as the app's `MerchantSession`. Notifies on every auth
 * state change, so the app root `sync`s on it.
 *
 * A token is read per call, never cached: an ID token expires, and
 * `getIdToken()` hands back the cached one and refreshes it only when it is
 * about to lapse, so asking per request costs nothing.
 */
export class FirebaseSession extends ChangeNotifier implements MerchantSession {
  readonly #auth: Auth;
  readonly #google = new GoogleAuthProvider();
  readonly #apple = new OAuthProvider("apple.com");
  #stage: SessionStage = "restoring";
  #email: string | null = null;
  #isAnonymous = false;

  constructor(auth: Auth) {
    super();
    this.#auth = auth;
    onAuthStateChanged(auth, (user) => {
      this.#stage = user ? "signedIn" : "signedOut";
      this.#email = user?.email ?? null;
      this.#isAnonymous = user?.isAnonymous ?? false;
      this.notifyListeners();
    });
  }

  get stage(): SessionStage {
    return this.#stage;
  }

  get email(): string | null {
    return this.#email;
  }

  get isAnonymous(): boolean {
    return this.#isAnonymous;
  }

  async authorizeCall(opts: { fresh?: boolean } = {}): Promise<ApiContext> {
    const token = await this.#auth.currentUser?.getIdToken(opts.fresh ?? false);
    return token ? withToken(token) : anonymous;
  }

  async signInWithEmail(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.#auth, email, password);
  }

  async signUpWithEmail(name: string, email: string, password: string): Promise<void> {
    const cred = await createUserWithEmailAndPassword(this.#auth, email, password);
    await updateProfile(cred.user, { displayName: name });
    await cred.user.getIdToken(true);
  }

  async signInWithGoogle(): Promise<void> {
    await signInWithPopup(this.#auth, this.#google);
  }

  async signInWithApple(): Promise<void> {
    await signInWithPopup(this.#auth, this.#apple);
  }

  async sendMagicLink(email: string): Promise<void> {
    await sendSignInLinkToEmail(this.#auth, email, {
      url: `${window.location.origin}/login`,
      handleCodeInApp: true,
    });
    window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
  }

  async completePendingSignIn(): Promise<void> {
    if (!isSignInWithEmailLink(this.#auth, window.location.href)) return;
    const email =
      window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY) ??
      window.prompt("Please confirm your email to finish signing in") ??
      "";
    if (!email) return;
    await signInWithEmailLink(this.#auth, email, window.location.href);
    window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(this.#auth);
  }
}
